import React, { useState } from "react";
import { Car, MapPin, Users, Clock, Phone, Package } from "lucide-react";
import { convertNumberToTimeAMPM } from "@ridecloud/shared";

const RideCard = ({ ride, onCall }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Calculate total available seats across all sections
  const getTotalAvailableSeats = () =>
    ride.vehicle.seats.front.available_seats +
    ride.vehicle.seats.middle.available_seats +
    ride.vehicle.seats.back.available_seats;

  const getTotalSeats = () =>
    ride.vehicle.seats.front.total_seats +
    ride.vehicle.seats.middle.total_seats +
    ride.vehicle.seats.back.total_seats;

  const getLowestPrice = () => {
    const prices = [];
    if (ride.vehicle.seats.front.available_seats > 0)
      prices.push(ride.vehicle.seats.front.price);
    if (ride.vehicle.seats.middle.available_seats > 0)
      prices.push(ride.vehicle.seats.middle.price);
    if (ride.vehicle.seats.back.available_seats > 0)
      prices.push(ride.vehicle.seats.back.price);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

  const totalAvailableSeats = getTotalAvailableSeats();
  const totalSeats = getTotalSeats();
  const lowestPrice = getLowestPrice();

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-emerald-200 group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <img
            src={ride.userId.pic}
            alt="Driver"
            className="w-12 h-12 rounded-full object-cover border-2 border-green-300"
          />
          <div>
            <h3 className="font-semibold text-gray-800 capitalize">
              {ride.userId.first_name + " " + ride.userId.last_name}
            </h3>

            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500">{ride.userId.phone}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-emerald-600">
            ₹{lowestPrice}
            {totalAvailableSeats > 0 && (
              <span className="text-sm text-gray-500">+</span>
            )}
          </div>
          <div className="text-sm text-gray-500">from</div>
        </div>
      </div>

      {/* Route */}
      <div className="mb-1 flex gap-3">
        {/* Timeline */}
        <div className="relative flex flex-col items-center">
          {/* Green dot (Pickup heading) */}
          <div className="w-4 h-4 rounded-full bg-emerald-500"></div>

          {/* Vertical line between pickup & dropoff */}
          <div className="flex-1 w-px bg-gray-300"></div>

          {/* Red dot (Drop-off heading) */}
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Pickup Points */}
          <div className="mb-4">
            <div className="font-medium text-gray-800 text-sm mb-1">
              Pickup Points:
            </div>
            {ride.pickupPoints.map((point, index) => (
              <div key={index} className="text-sm text-gray-600 ml-2">
                • {point}
              </div>
            ))}
          </div>

          {/* Drop-off Points */}

          <div className="font-medium text-gray-800 text-sm ">
            Drop-off Points:
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        {/* empty spacing */}
        <div className="relative flex flex-col items-center">
          <div className="w-3 h-3 "></div>
          <div className="flex-1 w-px"></div>
          <div className="w-3 h-3 "></div>
        </div>

        <div className="flex-1">
          {ride.dropoffPoints.map((point, index) => (
            <div key={index} className="text-sm text-gray-600 ml-2">
              • {point}
            </div>
          ))}
        </div>
      </div>

      {/* Trip Details */}
      <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
        <div className="flex items-center gap-3">
          <Clock color="#a124f5" className="h-4 w-4" />
          <span>{convertNumberToTimeAMPM(ride.time)}</span>
        </div>
        <div className="flex items-center gap-3">
          <Car color="#000000" />
          <span className="capitalize">{ride.vehicle.name}</span>
        </div>
      </div>

      {/* Seat Pricing */}
      <div className="mb-4 bg-gray-50 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-700 mb-2">
          Available Seats & Pricing:
        </div>
        <div className="space-y-1">
          {ride.vehicle.seats.front.available_seats > 0 && (
            <div className="flex justify-between text-xs">
              <span>
                Front ({ride.vehicle.seats.front.available_seats} seats)
              </span>
              <span className="font-medium">
                ₹{ride.vehicle.seats.front.price}
              </span>
            </div>
          )}
          {ride.vehicle.seats.middle.available_seats > 0 && (
            <div className="flex justify-between text-xs">
              <span>
                Middle ({ride.vehicle.seats.middle.available_seats} seats)
              </span>
              <span className="font-medium">
                ₹{ride.vehicle.seats.middle.price}
              </span>
            </div>
          )}
          {ride.vehicle.seats.back.available_seats > 0 && (
            <div className="flex justify-between text-xs">
              <span>
                Back ({ride.vehicle.seats.back.available_seats} seats)
              </span>
              <span className="font-medium">
                ₹{ride.vehicle.seats.back.price}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Seats and Luggage */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {totalAvailableSeats} of {totalSeats} seats available
            </span>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: totalSeats }).map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index < totalAvailableSeats ? "bg-emerald-400" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <Package className="h-3 w-3" />
            <span>{ride.luggage.no_of_luggage} luggage</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {ride.luggage.sizes.map((size, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full"
              >
                {size}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Book Button */}
      <button
        onClick={() => onCall(ride.userId.phone)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={totalAvailableSeats === 0 || !ride.isAvailable}
        className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${
          totalAvailableSeats === 0 || !ride.isAvailable
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 transform group-hover:scale-105"
        }`}
      >
        {totalAvailableSeats === 0 || !ride.isAvailable ? (
          "Fully Booked"
        ) : (
          <>
            <Phone className="w-4 h-4" /> &nbsp; Call the Person
            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
                {ride.userId.phone}
              </div>
            )}
          </>
        )}
      </button>
    </div>
  );
};

export default RideCard;
